"use client";

import React from "react";
import { motion } from "framer-motion";
import { Cpu, Search, Layers, PenTool, Code2, ShieldCheck, Rocket, HeartHandshake, Server, Database, Cloud, Terminal } from "lucide-react";

export const Act4HowWeEngineer: React.FC = () => {
  const STAGES = [
    {
      num: "01",
      title: "Discovery & Logic Audit",
      desc: "We analyze how your team handles leads, orders, and customer data to expose operational bottlenecks before writing a line of code.",
      icon: Search,
    },
    {
      num: "02",
      title: "Systems Architecture",
      desc: "We design resilient database schemas, API interfaces, and state-machine transitions that ensure zero data corruption under concurrency.",
      icon: Layers,
    },
    {
      num: "03",
      title: "High-Fidelity UI/UX",
      desc: "Fast, distraction-free interfaces engineered specifically for high-velocity staff workflows and seamless customer self-service.",
      icon: PenTool,
    },
    {
      num: "04",
      title: "Full-Stack Development",
      desc: "Modern TypeScript, Next.js, and high-throughput backend services engineered with clean architectural boundaries and complete test coverage.",
      icon: Code2,
    },
    {
      num: "05",
      title: "Automated QA & Security",
      desc: "Automated end-to-end integration tests, role-based permission audits, and vulnerability scans before touching production.",
      icon: ShieldCheck,
    },
    {
      num: "06",
      title: "Production Deployment",
      desc: "Zero-downtime rolling releases, containerized Docker environments, Cloudflare edge caching, and automated backup schedules.",
      icon: Rocket,
    },
    {
      num: "07",
      title: "24/7 SLA & Evolution",
      desc: "We don't hand off code and disappear. We continuously monitor server performance and upgrade your systems as your operations scale.",
      icon: HeartHandshake,
    },
  ];

  const TECH_TIERS = [
    {
      tier: "TIER 01 // FRONTEND & CLIENT INTERACTION",
      techs: ["Next.js 16 (App Router)", "React 19", "TypeScript", "Tailwind CSS v4", "Framer Motion"],
      icon: Terminal,
    },
    {
      tier: "TIER 02 // MOBILE & CROSS-PLATFORM APPS",
      techs: ["Flutter", "React Native", "Native iOS (Swift)", "Native Android (Kotlin)", "Offline-First SQLite"],
      icon: Layers,
    },
    {
      tier: "TIER 03 // HIGH-CONCURRENCY BACKEND APIS",
      techs: ["Node.js / Express", "FastAPI (Python)", "Go Microservices", "REST & GraphQL", "BullMQ / Redis Queues"],
      icon: Server,
    },
    {
      tier: "TIER 04 // DATA LEDGER & CACHING",
      techs: ["PostgreSQL", "Redis Enterprise", "MongoDB", "Isolated Multi-Tenant Schemas", "Zero Shared DB"],
      icon: Database,
    },
    {
      tier: "TIER 05 // CLOUD INFRASTRUCTURE & AUTOMATION",
      techs: ["Docker Containers", "AWS ECS / S3", "Cloudflare Edge", "WhatsApp Cloud API", "Autonomous Event Daemons"],
      icon: Cloud,
    },
  ];

  return (
    <section id="scene-engineering" className="relative w-full py-24 px-6 sm:px-8 bg-[#F8F8F6] text-[#111318] border-b border-[#111318]/10">
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 text-xs font-mono tracking-widest text-[#2563EB] uppercase font-semibold mb-3">
            <Cpu className="w-3.5 h-3.5" />
            <span>ENGINEERING BLUEPRINT</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-[#111318] tracking-tight mb-4">
            FROM IDEA TO PRODUCTION.
          </h2>
          <p className="text-base text-[#5E636B] leading-relaxed">
            Serious software requires an uncompromised methodology. Explore our documented 7-stage engineering lifecycle and modern architectural stack.
          </p>
        </div>

        {/* 7-Stage Architectural Blueprint Flow */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-20">
          {STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            return (
              <motion.div
                key={stage.num}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.06, duration: 0.5 }}
                className={`p-7 rounded-2xl bg-white border border-[#111318]/10 hover:border-[#2563EB]/40 transition-all shadow-[0_4px_20px_rgb(0,0,0,0.02)] ${
                  idx === 6 ? "md:col-span-2 lg:col-span-1" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-[#2563EB] font-bold bg-[#2563EB]/10 px-2.5 py-1 rounded">
                    STAGE {stage.num}
                  </span>
                  <Icon className="w-5 h-5 text-[#5E636B]" />
                </div>
                <h3 className="text-base font-bold text-[#111318] mb-2">{stage.title}</h3>
                <p className="text-xs text-[#5E636B] leading-relaxed">{stage.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Clean Blueprint Stack Breakdown (Not a logo wall) */}
        <div className="p-8 sm:p-12 rounded-3xl bg-white border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-6 border-b border-[#111318]/10">
            <div>
              <span className="text-xs font-mono tracking-widest text-[#5E636B] uppercase block mb-1 font-semibold">
                SYSTEM ARCHITECTURE TIERS
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#111318]">THE FULL-STACK BLUEPRINT</h3>
            </div>
            <p className="text-xs text-[#5E636B] max-w-sm leading-relaxed">
              Every system is engineered across strict architectural boundaries to allow independent scaling and prevent cascading outages.
            </p>
          </div>

          <div className="space-y-3.5">
            {TECH_TIERS.map((tier) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.tier}
                  className="p-5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 flex flex-col md:flex-row md:items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-lg bg-white border border-[#111318]/10 flex items-center justify-center shrink-0 shadow-sm">
                      <Icon className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <div>
                      <div className="text-xs font-mono text-[#5E636B] font-semibold">{tier.tier}</div>
                      <div className="text-sm font-bold text-[#111318]">Production Engineered</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {tier.techs.map((tech) => (
                      <span
                        key={tech}
                        className="text-xs font-mono px-3 py-1 rounded-md bg-white border border-[#111318]/10 text-[#111318] font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
